import { com, kotlin } from '@eidu/web'
import React, { useState } from 'react'
import PathField, { RenderablePath } from '../../pages/permission/path/PathField'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityPath = com.eidu.entity.path.EntityPath
import Field = com.eidu.entity.field.Field
import FieldType = com.eidu.entity.field.FieldType
import EdgeDirection = com.eidu.entity.path.EdgeDirection
import Edge = com.eidu.entity.path.Edge
import KtList = kotlin.collections.KtList
import FieldSingleSelect from './FieldSingleSelect'
import { isReferenceType } from '../misc/ReferenceTypeUtil'

type ComplexPathFieldProps = {
  entityTypeId: EntityTypeId
  targetType: EntityType | undefined
  selectedPath: EntityPath | undefined
  setSelectedPath: (path: EntityPath | undefined) => void
  renderablePaths: readonly RenderablePath[]
  fieldType: FieldType
  disabled?: boolean
}

type SplitPath = {
  path: EntityPath | undefined
  field: Field | undefined
}

const splitSelectedPath = (
  selectedPath: EntityPath | undefined,
  targetType: EntityType | undefined,
  fieldType: FieldType,
  renderablePaths: readonly RenderablePath[]
): SplitPath => {
  if (!selectedPath || !targetType) return { path: undefined, field: undefined }

  const edges = selectedPath.edges.asJsReadonlyArrayView()
  const lastEdge = edges.at(-1)
  const endsWithField =
    !isReferenceType(fieldType) || !renderablePaths.some(({ entityPath }) => entityPath.equals(selectedPath))
  if (!lastEdge || !endsWithField || !lastEdge.nextType.equals(targetType.id)) {
    return { path: selectedPath, field: undefined }
  }

  const field = targetType.fields.asJsReadonlyArrayView().find(({ id }) => id.equals(lastEdge.field))
  if (!field) return { path: selectedPath, field: undefined }

  const path =
    edges.length > 1
      ? selectedPath.copy(selectedPath.fromType, lastEdge.nextType, KtList.fromJsArray(edges.slice(0, -1)))
      : undefined
  return { path, field }
}

const joinPathAndField = (
  entityTypeId: EntityTypeId,
  targetType: EntityType,
  path: EntityPath | undefined,
  field: Field
): EntityPath => {
  const fieldEdge = new Edge(EdgeDirection.Outgoing, field.id, targetType.id)
  return path
    ? path.copy(path.fromType, targetType.id, KtList.fromJsArray([...path.edges.asJsReadonlyArrayView(), fieldEdge]))
    : new EntityPath(entityTypeId, targetType.id, KtList.fromJsArray([fieldEdge]))
}

const ComplexPathField = ({
  entityTypeId,
  targetType,
  selectedPath,
  setSelectedPath,
  renderablePaths,
  fieldType,
  disabled = false,
}: ComplexPathFieldProps) => {
  const [pendingPath, setPendingPath] = useState<EntityPath>()

  const split = splitSelectedPath(selectedPath, targetType, fieldType, renderablePaths)
  const validPendingPath =
    !isReferenceType(fieldType) && renderablePaths.some(({ entityPath }) => entityPath.equals(pendingPath))
      ? pendingPath
      : undefined
  const path = split.path ?? validPendingPath
  const targetFieldType = isReferenceType(fieldType) && split.field ? split.field.type : fieldType

  const selectPath = (newPath?: EntityPath) => {
    if (isReferenceType(fieldType)) {
      setSelectedPath(newPath)
    } else {
      setPendingPath(newPath)
      setSelectedPath(undefined)
    }
  }

  return (
    <>
      {targetType && !targetType.id.equals(entityTypeId) && (
        <PathField selectedPath={path} paths={renderablePaths} onChange={selectPath} disabled={disabled} />
      )}
      {targetType && (targetType.id.equals(entityTypeId) || path) && !isReferenceType(targetFieldType) && (
        <FieldSingleSelect
          label="Target field"
          options={targetType.fields.asJsReadonlyArrayView().filter(({ type }) => type === targetFieldType)}
          selected={split.field}
          setSelected={(field) => setSelectedPath(joinPathAndField(entityTypeId, targetType, path, field))}
          entityType={targetType}
          errorLabel={`Requires selecting a field of type '${targetFieldType}' but not found on type '${targetType.name}'.`}
          disabled={disabled}
        />
      )}
    </>
  )
}

export default ComplexPathField
